<template>
  <aside class="home-sidebar">

    
    <!-- Categories Section -->
    <div class="sidebar-section">
      <h3 class="sidebar-title">
        <i class="pi pi-tags"></i>
        {{ $t('sidebar.categories') }}
      </h3>
      
      <div v-if="categoriesLoading" class="loading-state">
        <div class="skeleton-item" v-for="i in 5" :key="i"></div>
      </div>
      
      <div v-else-if="categoriesError" class="error-state">
        <i class="pi pi-exclamation-triangle"></i>
        <p>{{ $t('sidebar.categoriesError') }}</p>
        <button @click="fetchCategories" class="retry-btn">
          <i class="pi pi-refresh"></i>
        </button>
      </div>
      
      <div v-else-if="categories.length === 0" class="empty-state">
        <i class="pi pi-inbox"></i>
        <p>{{ $t('sidebar.noCategories') }}</p>
      </div>
      
      <ul v-else class="categories-list">
        <li 
          v-for="category in categories" 
          :key="category.id"
          class="category-item"
          @click="filterByCategory(category)"
        >
          <span class="category-name">{{ category.name }}</span>
          <span v-if="category.book_count" class="book-count">
            {{ category.book_count }}
          </span>
        </li>
      </ul>
    </div>

    <!-- Authors Section -->
    <div class="sidebar-section">
      <h3 class="sidebar-title">
        <i class="pi pi-users"></i>
        {{ $t('sidebar.authors') }}
      </h3>
      
      <div v-if="authorsLoading" class="loading-state">
        <div class="skeleton-item" v-for="i in 5" :key="i"></div>
      </div>
      
      <div v-else-if="authorsError" class="error-state">
        <i class="pi pi-exclamation-triangle"></i>
        <p>{{ $t('sidebar.authorsError') }}</p>
        <button @click="fetchAuthors" class="retry-btn">
          <i class="pi pi-refresh"></i>
        </button>
      </div>
      
      <div v-else-if="authors.length === 0" class="empty-state">
        <i class="pi pi-user"></i>
        <p>{{ $t('sidebar.noAuthors') }}</p>
      </div>
      
      <ul v-else class="authors-list">
        <li 
          v-for="author in authors" 
          :key="author.id"
          class="author-item"
          @click="filterByAuthor(author)"
        >
          <div class="author-info">
            <span class="author-name">{{ author.name }}</span>
            <span v-if="author.book_count" class="book-count">
              {{ author.book_count }}
            </span>
          </div>
          <p v-if="author.bio" class="author-bio">{{ author.bio }}</p>
        </li>
      </ul>
    </div>

    <!-- Quick Actions -->
    <div class="sidebar-section">
      <h3 class="sidebar-title">
        <i class="pi pi-bolt"></i>
        {{ $t('sidebar.quickActions') }}
      </h3>
      
      <div class="quick-actions">
        <router-link to="/books" class="quick-action-btn">
          <i class="pi pi-book"></i>
          {{ $t('sidebar.browseAllBooks') }}
        </router-link>
        
        <router-link to="/subscription" class="quick-action-btn">
          <i class="pi pi-star"></i>
          {{ $t('sidebar.viewPlans') }}
        </router-link>
        
        <button @click="showRandomBook" class="quick-action-btn random-book-btn">
          <i class="pi pi-refresh"></i>
          {{ $t('sidebar.randomBook') }}
        </button>
      </div>
    </div>
  </aside>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'

export default defineComponent({
  name: 'HomeSidebar',
  emits: ['category-filter', 'author-filter'],
  setup(props, { emit }) {
    const router = useRouter()
    
    // State
    const categories = ref([])
    const authors = ref([])
    const categoriesLoading = ref(false)
    const authorsLoading = ref(false)
    const categoriesError = ref(null)
    const authorsError = ref(null)

    // Methods
    const fetchCategories = async () => {
      categoriesLoading.value = true
      categoriesError.value = null
      
      try {
        const response = await api.getCategories()
        categories.value = response.data.results || response.data || []
      } catch (error) {
        categoriesError.value = 'Failed to load categories'
        categories.value = []
      } finally {
        categoriesLoading.value = false
      }
    }

    const fetchAuthors = async () => {
      authorsLoading.value = true
      authorsError.value = null
      
      try {
        const response = await api.getAuthors()
        authors.value = response.data.results || response.data || []
      } catch (error) {
        authorsError.value = 'Failed to load authors'
        authors.value = []
      } finally {
        authorsLoading.value = false
      }
    }

    const filterByCategory = (category) => {
      emit('category-filter', category)
      router.push({ 
        path: '/books',
        query: { category: category.slug } 
      })
    }

    const filterByAuthor = (author) => {
      emit('author-filter', author)
      router.push({ 
        path: '/books',
        query: { author: author.id } 
      })
    }

    const showRandomBook = async () => {
      try {
        // Get a reasonable number of books to pick from randomly
        // Using page_size to limit the number of books fetched
        const response = await api.getBooks({ page_size: 50 })
        const books = response.data.results || response.data || []
        
        if (books.length > 0) {
          // Pick a random book from the results
          const randomIndex = Math.floor(Math.random() * books.length)
          const randomBook = books[randomIndex]
          
          // Navigate to the random book's read page
          router.push(`/read/${randomBook.id}`)
        } else {
          // If no books, just go to books page
          router.push('/books')
        }
      } catch (error) {
        console.error('Failed to get random book:', error)
        // Fallback to books page
        router.push('/books')
      }
    }



    // Lifecycle
    onMounted(() => {
      fetchCategories()
      fetchAuthors()
    })

    return {
      // State
      categories,
      authors,
      categoriesLoading,
      authorsLoading,
      categoriesError,
      authorsError,
      
      // Methods
      fetchCategories,
      fetchAuthors,
      filterByCategory,
      filterByAuthor,
      showRandomBook
    }
  }
})
</script>

<style lang="scss" scoped>
.home-sidebar {
  width: 280px;
  background: white;
  border-right: 1px solid #e5e7eb;
  padding: 1.5rem;
  height: 100%;
  overflow-y: auto;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 1024px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    height: auto;
    overflow-y: visible;
  }
}

.sidebar-section {
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.sidebar-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e5e7eb;
  
  i {
    color: var(--primary-color, #3b82f6);
  }
}

// Categories
.categories-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: #f9fafb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
    transform: translateX(4px);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
}

.category-name {
  font-weight: 500;
  color: #374151;
}

.book-count {
  background: var(--primary-color, #3b82f6);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 24px;
  text-align: center;
}

// Authors
.authors-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.author-item {
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
    transform: translateX(4px);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
}

.author-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.author-name {
  font-weight: 600;
  color: #374151;
}

.author-bio {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

// Quick Actions
.quick-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.quick-action-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--primary-color, #3b82f6);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  
  &:hover {
    background: var(--primary-dark, #2563eb);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  i {
    font-size: 1rem;
  }
}

// Loading States
.loading-state {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.skeleton-item {
  height: 40px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 8px;
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Error States
.error-state {
  text-align: center;
  padding: 1rem;
  color: #dc2626;
  
  i {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    margin: 0.5rem 0;
    font-size: 0.875rem;
  }
}

.retry-btn {
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #b91c1c;
  }
}

// Empty States
.empty-state {
  text-align: center;
  padding: 1rem;
  color: #6b7280;
  
  i {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    margin: 0;
    font-size: 0.875rem;
  }
}

// Random Book Button with Vazirematn font for Uyghur RTL
.random-book-btn {
  // Default font for LTR languages
  font-family: inherit;
  
  // Vazirematn font for Uyghur RTL (Arabic locale)
  [dir="rtl"] &,
  .rtl & {
    font-family: 'Vazirematn', 'Vazir', 'Tahoma', sans-serif;
  }
}

// Responsive Design
@media (max-width: 1024px) {
  .home-sidebar {
    padding: 1rem;
  }
  
  .sidebar-section {
    margin-bottom: 1.5rem;
  }
  
  .categories-list,
  .authors-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }
  
  .quick-actions {
    flex-direction: row;
    flex-wrap: wrap;
    
    .quick-action-btn {
      flex: 1;
      min-width: 150px;
    }
  }
}

@media (max-width: 768px) {
  .home-sidebar {
    padding: 0.75rem;
  }
  
  .sidebar-title {
    font-size: 1rem;
  }
  
  .categories-list,
  .authors-list {
    grid-template-columns: 1fr;
  }
  
  .quick-actions {
    flex-direction: column;
    
    .quick-action-btn {
      min-width: auto;
    }
  }
}
</style>
